/**
 * Self-Service Signup Dashboard
 * Displays and manages organizations created through self-service signup
 * Shows demo booking status and conversion metrics
 */

import React, { useState, useEffect } from 'react';
import { Building2, Calendar, CheckCircle, Clock, XCircle, TrendingUp, Users, Mail, Phone, Settings, ExternalLink, Copy, Check as CheckIcon } from 'lucide-react';
import { organizationsAPI } from '@/services/api/organizations';
import { apiClient } from '@/services/apiClient';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useTabbedChrome } from '@/components/weavein';
interface SelfSignupOrg {
  cbo_id: number;
  organization_name: string;
  contact_email: string;
  org_status: string;
  created_at: string;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  signup_date: string;
  demo_booked: boolean;
  demo_booked_at: string | null;
  demo_completed: boolean;
  demo_completed_at: string | null;
  converted_to_paid: boolean;
  converted_at: string | null;
  login_count: number;
  last_login_at: string | null;
  email_domain: string;
  next_demo_time: string | null;
  demo_status: string | null;
  is_self_signup: boolean;
}

// Default Calendly URL - can be updated by admin
const DEFAULT_CALENDLY_URL = 'https://calendly.com/weavein-demo/30min';

export const SelfSignupDashboard: React.FC = () => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [organizations, setOrganizations] = useState<SelfSignupOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'demo_pending' | 'demo_booked' | 'converted'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Calendly URL configuration state
  const [calendlyUrl, setCalendlyUrl] = useState(DEFAULT_CALENDLY_URL);
  const [editingCalendly, setEditingCalendly] = useState(false);
  const [tempCalendlyUrl, setTempCalendlyUrl] = useState('');
  const [savingCalendly, setSavingCalendly] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCalendlySettings, setShowCalendlySettings] = useState(false);

  useEffect(() => {
    loadSelfSignupOrgs();
    loadCalendlyUrl();
  }, []);

  // Load Calendly URL from platform settings
  const loadCalendlyUrl = async () => {
    try {
      const response = await apiClient.get('/api/platform-settings/calendly-url');
      if (response.data?.url) {
        setCalendlyUrl(response.data.url);
      }
    } catch (error) {
      // Use default URL if not configured or API fails
      console.log('Using default Calendly URL');
    }
  };

  // Save Calendly URL to platform settings
  const saveCalendlyUrl = async () => {
    if (!tempCalendlyUrl.trim()) return;

    try {
      setSavingCalendly(true);
      await apiClient.put('/api/platform-settings/calendly-url', { url: tempCalendlyUrl.trim() });
      setCalendlyUrl(tempCalendlyUrl.trim());
      setEditingCalendly(false);
    } catch (error) {
      console.error('Failed to save Calendly URL:', error);
      alert('Failed to save Calendly URL. Please try again.');
    } finally {
      setSavingCalendly(false);
    }
  };

  // Copy Calendly URL to clipboard
  const copyCalendlyUrl = () => {
    navigator.clipboard.writeText(calendlyUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSelfSignupOrgs = async () => {
    try {
      setLoading(true);
      // Get all organizations and filter for self-signup ones
      const allOrgs = await organizationsAPI.listOrganizations();

      // Filter for self-signup organizations and map to expected format
      const selfSignupOrgs = allOrgs
        .filter((org: any) => {
          try {
            const settings = typeof org.settings === 'string' ? JSON.parse(org.settings) : org.settings;
            return settings?.self_signup === true;
          } catch {
            return false;
          }
        })
        .map((org: any) => {
          // Parse settings to get signup details
          let settings: any = {};
          try {
            settings = typeof org.settings === 'string' ? JSON.parse(org.settings) : (org.settings || {});
          } catch {
            settings = {};
          }

          // Map organization data to SelfSignupOrg interface
          return {
            cbo_id: org.id,
            organization_name: org.name || 'Unknown Organization',
            contact_email: org.contact_email || '',
            org_status: org.status || 'pending',
            created_at: org.created_ts || org.created_at || new Date().toISOString(),
            // User info - use contact email as fallback since we don't have user details
            user_id: 0,
            user_name: org.contact_email?.split('@')[0] || 'Unknown',
            user_email: org.contact_email || '',
            user_role: 'viewer',
            // Signup metadata from settings
            signup_date: settings.signup_date || org.created_ts || new Date().toISOString(),
            demo_booked: settings.demo_booked || false,
            demo_booked_at: settings.demo_booked_at || null,
            demo_completed: settings.demo_completed || false,
            demo_completed_at: settings.demo_completed_at || null,
            converted_to_paid: settings.converted_to_paid || false,
            converted_at: settings.converted_at || null,
            login_count: settings.login_count || 0,
            last_login_at: settings.last_login_at || null,
            email_domain: org.contact_email?.split('@')[1] || '',
            next_demo_time: settings.next_demo_time || null,
            demo_status: settings.demo_status || null,
            is_self_signup: true,
          } as SelfSignupOrg;
        });

      setOrganizations(selfSignupOrgs);
    } catch (error) {
      console.error('Failed to load self-signup organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrgs = organizations.filter(org => {
    // Apply status filter
    if (filter === 'demo_pending' && org.demo_booked) return false;
    if (filter === 'demo_booked' && !org.demo_booked) return false;
    if (filter === 'converted' && !org.converted_to_paid) return false;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        org.organization_name?.toLowerCase().includes(search) ||
        org.user_email?.toLowerCase().includes(search) ||
        org.email_domain?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  // Calculate metrics
  const metrics = {
    total: organizations.length,
    demoPending: organizations.filter(org => !org.demo_booked).length,
    demoBooked: organizations.filter(org => org.demo_booked && !org.demo_completed).length,
    demoCompleted: organizations.filter(org => org.demo_completed).length,
    converted: organizations.filter(org => org.converted_to_paid).length,
    conversionRate: organizations.length > 0
      ? ((organizations.filter(org => org.converted_to_paid).length / organizations.length) * 100).toFixed(1)
      : '0',
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return fmt.medium(dateString);
  };

  const getStatusBadge = (org: SelfSignupOrg) => {
    if (org.converted_to_paid) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-medium">{t('admin.converted', 'Converted')}</span>;
    }
    if (org.demo_completed) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 font-medium">{t('admin.demo_complete', 'Demo Complete')}</span>;
    }
    if (org.demo_booked) {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium">{t('admin.demo_scheduled', 'Demo Scheduled')}</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 font-medium">{t('admin.pending_demo', 'Pending Demo')}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">{t('admin.loading_selfsignup_organizations', 'Loading self-signup organizations...')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('admin.selfservice_signups', 'Self-Service Signups')}</h2>}
          <p className="text-sm text-gray-600 mt-1">
            {t('admin.organizations_created_through_selfservice_signup', 'Organizations created through self-service signup flow')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCalendlySettings(!showCalendlySettings)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            <Settings className="w-4 h-4" />
            {t('admin.calendly_settings', 'Calendly Settings')}
          </button>
          <button
            onClick={loadSelfSignupOrgs}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            {t('admin.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Calendly URL Settings */}
      {showCalendlySettings && (
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 rounded-lg shadow p-6 border border-teal-200 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('admin.demo_scheduling_settings', 'Demo Scheduling Settings')}
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {t('admin.calendly_description', 'Configure the Calendly URL that new viewer accounts will use to schedule demos. This URL is displayed on the Viewer Capabilities page.')}
          </p>

          {editingCalendly ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.calendly_url', 'Calendly URL')}
                </label>
                <input
                  type="url"
                  value={tempCalendlyUrl}
                  onChange={(e) => setTempCalendlyUrl(e.target.value)}
                  placeholder="https://calendly.com/your-link/30min"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={saveCalendlyUrl}
                  disabled={savingCalendly || !tempCalendlyUrl.trim()}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCalendly ? t('admin.saving', 'Saving...') : t('admin.save', 'Save')}
                </button>
                <button
                  onClick={() => {
                    setEditingCalendly(false);
                    setTempCalendlyUrl('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200"
                >
                  {t('admin.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.current_calendly_url', 'Current Calendly URL')}
                </p>
                <a
                  href={calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-teal-700 dark:text-teal-400 text-sm truncate block"
                >
                  {calendlyUrl}
                  <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={copyCalendlyUrl}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title={t('admin.copy_url', 'Copy URL')}
                >
                  {copied ? <CheckIcon className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => {
                    setTempCalendlyUrl(calendlyUrl);
                    setEditingCalendly(true);
                  }}
                  className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 dark:bg-teal-900 dark:text-teal-300 text-sm font-medium"
                >
                  {t('admin.edit', 'Edit')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('admin.total_signups', 'Total Signups')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.total}</p>
            </div>
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('admin.demo_pending', 'Demo Pending')}</p>
              <p className="text-2xl font-bold text-yellow-600">{metrics.demoPending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('admin.demo_booked', 'Demo Booked')}</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.demoBooked}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('admin.demo_complete_1', 'Demo Complete')}</p>
              <p className="text-2xl font-bold text-green-600">{metrics.demoCompleted}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('admin.converted_1', 'Converted')}</p>
              <p className="text-2xl font-bold text-green-600">{metrics.converted}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{metrics.conversionRate}% rate</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={t('admin.search_by_organization_email_or', 'Search by organization, email, or domain...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('admin.all', 'All')}
            </button>
            <button
              onClick={() => setFilter('demo_pending')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'demo_pending'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('admin.pending', 'Pending')}
            </button>
            <button
              onClick={() => setFilter('demo_booked')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'demo_booked'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('admin.booked', 'Booked')}
            </button>
            <button
              onClick={() => setFilter('converted')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'converted'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('admin.converted_2', 'Converted')}
            </button>
          </div>
        </div>
      </div>

      {/* Organizations List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.organization', 'Organization')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.contact', 'Contact')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.status', 'Status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.signup_date', 'Signup Date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.demo', 'Demo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.activity', 'Activity')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {filteredOrgs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('admin.no_organizations_found', 'No organizations found')}
                  </td>
                </tr>
              ) : (
                filteredOrgs.map((org) => (
                  <tr key={org.cbo_id} className="hover:bg-gray-50 dark:bg-gray-700">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {org.organization_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            ID: {org.cbo_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">{org.user_name}</div>
                      <div className="text-xs text-gray-500 flex items-center mt-1">
                        <Mail className="w-3 h-3 mr-1" />
                        {org.user_email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(org)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(org.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      {org.demo_booked ? (
                        <div>
                          <div className="text-sm text-gray-900 dark:text-white">
                            {formatDate(org.demo_booked_at)}
                          </div>
                          {org.next_demo_time && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(org.next_demo_time)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">{t('admin.not_scheduled', 'Not scheduled')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {org.login_count || 0} logins
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {org.last_login_at ? `Last: ${formatDate(org.last_login_at)}` : 'Never'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SelfSignupDashboard;
